import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import useAuthToken from 'utils/useAuthToken';
import { SignUpTypes } from 'interfaces';
import { featureByName, signup } from 'actions/app.actions';
import { featureSelector } from 'reducers/app.reducer';
import { api } from 'services/api';
import { PASSWORD_REGEX, ROUTES } from '../../constants';
import SignUpForm from './SignUpForm';

const initialValues: SignUpTypes = {
  confirmPassword: '',
  username: '',
  email: '',
  clientId: '',
  firstName: '',
  gender: '',
  lastName: '',
  optin: false,
  password: '',
  terms: false,
  companyName: '',
};

const SignUp = (): JSX.Element => {
  const appFeatures = useSelector(featureSelector('post_user_register'));
  const referralFeature = useSelector(featureSelector('referral'));
  const isReferralFeatureEnable = referralFeature?.data?.enabled;
  const isCompanyNameRequired = appFeatures?.data?.attrs.company_name_required;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { setCookie } = useAuthToken();

  const passwordValidator = Yup.string()
    .required(t('core.required'))
    .matches(PASSWORD_REGEX, t('core.password.characters', { min: 14 }));

  const userValidationSchemaWithCompany = Yup.object().shape({
    confirmPassword: passwordValidator.oneOf(
      [Yup.ref('password')],
      t('core.password.mismatch'),
    ),
    username: Yup.string()
      .trim()
      .email(t('core.invalid_email_address'))
      .required(t('core.required'))
      .test(
        'emailAlreadyUsed',
        t('core.invalid_email_already_exists'),
        async function (value) {
          try {
            const response = await api.post('/users/check-email', {
              email: value,
            });
            return response.data.status;
          } catch (error) {
            return false;
          }
        },
      ),
    clientId: Yup.string()
      .required(t('core.required'))
      .test(
        'clientIdAlreadyUsed',
        t('core.invalid_unique_code_already_exists'),
        async function (value) {
          try {
            const response = await api.post('/users/check-matricule', {
              clientId: value,
            });
            return response.data.status;
          } catch (error) {
            return false;
          }
        },
      ),
    firstName: Yup.string()
      .required(t('core.required'))
      .min(2, t('core.min', { min: 2 })),
    gender: Yup.number().required(t('core.required')),
    lastName: Yup.string()
      .required(t('core.required'))
      .min(2, t('core.min', { min: 2 })),
    companyName: Yup.string().required(t('core.required')),
    optin: Yup.boolean().required(t('core.required')),
    password: passwordValidator,
    terms: Yup.boolean()
      .required(t('auth.signup.required_terms_and_conditions'))
      .oneOf([true], t('auth.signup.required_terms_and_conditions')),
    referralCode: Yup.lazy(() => {
      if (isReferralFeatureEnable) {
        return Yup.string()
          .nullable()
          .matches(/^\d+-[A-Z0-9]{8}$/, t('auth.signup.referral.errorMessage'))
          .test(
            'referralCodeValid',
            t('auth.signup.referral.referralDoesNotExist'),
            async function (value) {
              if (!value) return true;
              try {
                const response = await api.post('/users/check-referral-code', {
                  referralCode: value,
                });
                return response.data.message;
              } catch (error) {
                return false;
              }
            },
          );
      }
      return Yup.string().notRequired();
    }),
  });

  const userValidationSchemaWithoutCompany = Yup.object().shape({
    confirmPassword: passwordValidator.oneOf(
      [Yup.ref('password')],
      t('core.password.mismatch'),
    ),
    username: Yup.string()
      .trim()
      .email(t('core.invalid_email_address'))
      .required(t('core.required'))
      .test(
        'emailAlreadyUsed',
        t('core.invalid_email_already_exists'),
        async function (value) {
          try {
            const response = await api.post('/users/check-email', {
              email: value,
            });
            return response.data.status;
          } catch (error) {
            return false;
          }
        },
      ),
    clientId: Yup.string()
      .required(t('core.required'))
      .test(
        'clientIdAlreadyUsed',
        t('core.invalid_unique_code_already_exists'),
        async function (value) {
          try {
            const response = await api.post('/users/check-matricule', {
              clientId: value,
            });
            return response.data.status;
          } catch (error) {
            return false;
          }
        },
      ),
    firstName: Yup.string()
      .required(t('core.required'))
      .min(2, t('core.min', { min: 2 })),
    gender: Yup.number().required(t('core.required')),
    lastName: Yup.string()
      .required(t('core.required'))
      .min(2, t('core.min', { min: 2 })),
    optin: Yup.boolean().required(t('core.required')),
    password: passwordValidator,
    terms: Yup.boolean()
      .required(t('auth.signup.required_terms_and_conditions'))
      .oneOf([true], t('auth.signup.required_terms_and_conditions')),
    referralCode: Yup.lazy(() => {
      if (isReferralFeatureEnable) {
        return Yup.string()
          .nullable()
          .matches(/^\d+-[A-Z0-9]{8}$/, t('auth.signup.referral.errorMessage'))
          .test(
            'referralCodeValid',
            t('auth.signup.referral.referralDoesNotExist'),
            async function (value) {
              if (!value) return true;
              try {
                const response = await api.post('/users/check-referral-code', {
                  referralCode: value,
                });
                return response.data.message;
              } catch (error) {
                return false;
              }
            },
          );
      }
      return Yup.string().notRequired();
    }),
  });

  /**
   * Return SignUpTypes without confirmPassword, terms and
   * change optinType(boolean) to 1 | 2 type for the API
   * @param values
   */
  const mapValues = (values: SignUpTypes): SignUpTypes => {
    const {
      confirmPassword, // eslint-disable-line @typescript-eslint/no-unused-vars
      // exclude confirmPassword to NOT send it to API
      terms,
      optin,
      username,
      ...rest
    } = values;
    const lsOptin = optin ? 1 : 2;
    const email = username.trim();
    return {
      optin: lsOptin,
      ...rest,
      username: email,
      email,
      isCGUAccepted: terms || false,
    };
  };

  const handleSignUp = (values: SignUpTypes): void => {
    const mappedValues: SignUpTypes = mapValues(values);
    if (values.terms) {
      dispatch(
        signup.request(mappedValues, (token) => {
          setCookie(token);
          history.push(ROUTES.HOME);
        }),
      );
    }
  };

  useEffect(() => {
    dispatch(featureByName.request('post_user_register'));
    dispatch(featureByName.request('referral'));
  }, []);

  return (
    <SignUpForm
      handleSignUp={handleSignUp}
      initialValues={initialValues}
      userValidationSchema={
        isCompanyNameRequired
          ? userValidationSchemaWithCompany
          : userValidationSchemaWithoutCompany
      }
      isCompanyNameRequired={isCompanyNameRequired}
      isReferralFeatureEnable={isReferralFeatureEnable}
    />
  );
};

export default SignUp;
